import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box, Paper, Typography, Chip, Menu, MenuItem, Button,
  CircularProgress, Collapse, Divider, Tooltip, Dialog,
  DialogTitle, DialogContent, IconButton, List, ListItem,
  ListItemButton, ListItemText, TextField, InputAdornment,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SyncIcon from '@mui/icons-material/Sync';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SearchIcon from '@mui/icons-material/Search';
import {
  getTenableStatus, syncVulns, syncAssets, getVulns, getVulnSummary,
  markVulnPatched, getCredentialedScan, getPluginList,
} from '../api/tenable';
import { uploadScanFile, importScanFile, listSampleFiles } from '../api/scans';
import VulnDeviceView from '../components/VulnDeviceView';

const SEV_COLOR = { critical: '#f44336', high: '#ff9800', medium: '#ffeb3b', low: '#4caf50', info: '#607d8b' };
const SEV_LABEL = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low', info: 'Info' };
const THRESHOLD = 98;

const CATEGORY_LABEL = {
  good_local_checks_enabled: 'Local Checks Enabled',
  good_unsupported_os:       'Unsupported OS (Exempt)',
  good_id_issues:            'Protocol/ID Issues (Exempt)',
  good_fragile_device:       'Fragile Device (Exempt)',
  remediate_some_failed:     'Local Checks Enabled (With Issues)',
  bad_local_checks_failed:   'Local Checks Failed',
  bad_no_credentials:        'No Credentials Provided',
  suspect:                   'Suspect — Needs Investigation',
};

// ── ACAS Coverage Banner ───────────────────────────────────────────────────────

function CredentialedBanner({ data, onViewDetails }) {
  if (!data) return null;

  const { x_count, y_count, coverage_rate, failure_rate, total, categories } = data;
  const denom   = x_count + y_count;
  const suspect = categories?.suspect || 0;
  const color   = coverage_rate >= THRESHOLD ? '#4caf50' : coverage_rate >= 83 ? '#ff9800' : '#f44336';
  const bgColor = coverage_rate >= THRESHOLD ? '#0a2a0a' : coverage_rate >= 83 ? '#2a1a00' : '#2a0a0a';
  const borderColor = coverage_rate >= THRESHOLD ? '#2a5a2a' : coverage_rate >= 83 ? '#5a3a00' : '#5a1a1a';

  return (
    <Paper onClick={onViewDetails} sx={{
      backgroundColor: bgColor,
      border: `1px solid ${borderColor}`,
      borderLeft: `4px solid ${color}`,
      borderRadius: 2, mb: 3, p: '16px 20px',
      cursor: 'pointer',
      '&:hover': { borderColor: color, filter: 'brightness(1.08)' },
      transition: 'all 0.15s',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>

        {/* Icon + Label */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {coverage_rate >= THRESHOLD
            ? <CheckCircleIcon sx={{ color, fontSize: 28 }} />
            : <ErrorIcon sx={{ color, fontSize: 28 }} />}
          <Box>
            <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: 13, lineHeight: 1 }}>
              19506 - Credentialed Scan
            </Typography>
            <Typography sx={{ color: '#7a8fa6', fontSize: 11, mt: 0.25 }}>
              {coverage_rate}% credentialed · CORA threshold: ≥{THRESHOLD}% · {total} hosts scanned · Click for details
            </Typography>
          </Box>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ borderColor: borderColor }} />

        {/* X/denom */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ color, fontSize: 32, fontWeight: 700, lineHeight: 1, fontFamily: 'monospace' }}>
            {x_count}/{denom}
          </Typography>
          <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mt: 0.25 }}>
            Credentialed Hosts
          </Typography>
        </Box>

        {/* Coverage rate */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ color, fontSize: 36, fontWeight: 700, lineHeight: 1 }}>
            {coverage_rate}%
          </Typography>
          <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mt: 0.25 }}>
            {coverage_rate >= THRESHOLD ? 'Meets CORA Threshold' : `${(THRESHOLD - coverage_rate).toFixed(1)}% Below Threshold`}
          </Typography>
        </Box>

        {/* Y (bad) */}
        {y_count > 0 && (
          <>
            <Divider orientation="vertical" flexItem sx={{ borderColor: borderColor }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ color: '#f44336', fontSize: 28, fontWeight: 700, lineHeight: 1 }}>{y_count}</Typography>
              <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mt: 0.25 }}>
                Y — Bad / No Creds
              </Typography>
            </Box>
          </>
        )}

        {/* Failure rate */}
        {denom > 0 && (
          <>
            <Divider orientation="vertical" flexItem sx={{ borderColor: borderColor }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ color: failure_rate <= 2 ? '#4caf50' : '#f44336', fontSize: 22, fontWeight: 700, lineHeight: 1 }}>
                {failure_rate}%
              </Typography>
              <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mt: 0.25 }}>
                Failure Rate
              </Typography>
            </Box>
          </>
        )}

        {/* Suspect count */}
        {suspect > 0 && (
          <>
            <Divider orientation="vertical" flexItem sx={{ borderColor: borderColor }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ color: '#ff9800', fontSize: 22, fontWeight: 700, lineHeight: 1 }}>{suspect}</Typography>
              <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mt: 0.25 }}>
                Suspect / Investigate
              </Typography>
            </Box>
          </>
        )}

        <Box sx={{ ml: 'auto' }}>
          <Chip label="View Details →" size="small" sx={{ backgroundColor: `${color}22`, color, fontWeight: 700, fontSize: 11, cursor: 'pointer' }} />
        </Box>
      </Box>
    </Paper>
  );
}

// ── ACAS Coverage Detail Dialog ────────────────────────────────────────────────

function CredentialedDialog({ open, onClose, data }) {
  const [tab, setTab] = useState('bad');
  if (!data) return null;

  const { hosts, coverage_rate, x_count, y_count, failure_rate, categories } = data;
  const bad       = hosts.filter(h => h.status === 'bad');
  const remediate = hosts.filter(h => h.status === 'remediate');
  const suspect   = hosts.filter(h => h.status === 'suspect');
  const good      = hosts.filter(h => h.status === 'good');
  const color     = coverage_rate >= THRESHOLD ? '#4caf50' : coverage_rate >= 83 ? '#ff9800' : '#f44336';
  const denom     = x_count + y_count;

  const tabs = [
    { key: 'bad',       label: `Bad / No Creds (${bad.length})`,       color: '#f44336' },
    { key: 'remediate', label: `Remediate (${remediate.length})`,       color: '#ff9800' },
    { key: 'suspect',   label: `Suspect (${suspect.length})`,           color: '#ff9800' },
    { key: 'good',      label: `Good / Exempt (${good.length})`,        color: '#4caf50' },
  ];

  const HostRow = ({ h, accentColor }) => (
    <Paper sx={{ backgroundColor: '#0d1825', border: `1px solid ${accentColor}22`, borderLeft: `3px solid ${accentColor}`, borderRadius: 1, p: '10px 14px', mb: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {accentColor === '#4caf50'
          ? <CheckCircleIcon sx={{ color: accentColor, fontSize: 16, mt: 0.3, flexShrink: 0 }} />
          : <ErrorIcon sx={{ color: accentColor, fontSize: 16, mt: 0.3, flexShrink: 0 }} />}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography sx={{ color: '#c0cfe0', fontFamily: 'monospace', fontSize: 13, fontWeight: 700 }}>{h.ip || '—'}</Typography>
            {h.hostname && h.hostname !== h.ip && (
              <Typography sx={{ color: '#7a8fa6', fontSize: 12 }}>{h.hostname}</Typography>
            )}
            <Chip label={CATEGORY_LABEL[h.category] || h.category} size="small"
              sx={{ backgroundColor: `${accentColor}18`, color: accentColor, fontSize: 10, height: 18 }} />
          </Box>
          {h.reason && (
            <Typography sx={{ color: accentColor === '#4caf50' ? '#7a8fa6' : accentColor, fontSize: 12, mt: 0.25 }}>{h.reason}</Typography>
          )}
        </Box>
        {h.last_seen && (
          <Typography sx={{ color: '#4a5a70', fontSize: 11, flexShrink: 0 }}>
            {new Date(h.last_seen).toLocaleDateString()}
          </Typography>
        )}
      </Box>
    </Paper>
  );

  const tabHosts = { bad, remediate, suspect, good };
  const tabColor = { bad: '#f44336', remediate: '#ff9800', suspect: '#ff9800', good: '#4caf50' };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f' } }}>
      <DialogTitle sx={{ color: '#ffffff', borderBottom: '1px solid #1e3a5f', pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ fontWeight: 700 }}>19506 - Credentialed Scan</Typography>
              <Chip label={`${coverage_rate}%`} size="small" sx={{ backgroundColor: `${color}22`, color, fontWeight: 700 }} />
            </Box>
            <Typography sx={{ color: '#7a8fa6', fontSize: 11, mt: 0.5 }}>
              {coverage_rate}% credentialed · {x_count} good · {y_count} uncredentialed · {failure_rate}% failure rate
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#7a8fa6' }}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', borderBottom: '1px solid #1e3a5f' }}>
          {tabs.map(t => (
            <Box key={t.key} onClick={() => setTab(t.key)} sx={{
              px: 2.5, py: 1.25, cursor: 'pointer',
              borderBottom: `2px solid ${tab === t.key ? t.color : 'transparent'}`,
              color: tab === t.key ? t.color : '#7a8fa6',
              fontWeight: tab === t.key ? 700 : 400,
              fontSize: 12,
              '&:hover': { color: t.color },
            }}>
              {t.label}
            </Box>
          ))}
        </Box>

        {/* Category breakdown */}
        <Box sx={{ display: 'flex', gap: 1.5, p: '10px 16px', borderBottom: '1px solid #1e3a5f', flexWrap: 'wrap' }}>
          {Object.entries(categories || {}).filter(([, v]) => v > 0).map(([cat, count]) => {
            const isGood = cat.startsWith('good');
            const isRem  = cat === 'remediate_some_failed';
            const isBad  = cat.startsWith('bad');
            const col    = isGood ? '#4caf50' : isRem ? '#ff9800' : isBad ? '#f44336' : '#ff9800';
            return (
              <Chip key={cat} label={`${CATEGORY_LABEL[cat] || cat}: ${count}`} size="small"
                sx={{ backgroundColor: `${col}15`, color: col, fontSize: 10, height: 20 }} />
            );
          })}
        </Box>

        <Box sx={{ p: 2, maxHeight: 420, overflowY: 'auto' }}>
          {(tabHosts[tab] || []).length === 0
            ? <Typography sx={{ color: '#4a5a70', p: 2, textAlign: 'center', fontStyle: 'italic' }}>
                No hosts in this category.
              </Typography>
            : (tabHosts[tab] || []).map((h, i) => (
                <HostRow key={i} h={h} accentColor={tabColor[tab]} />
              ))
          }
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ── Plugin Filter Panel ────────────────────────────────────────────────────────

function PluginFilter({ plugins, selectedPlugin, onSelect }) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() =>
    plugins.filter(p =>
      !search ||
      p.plugin_id.includes(search) ||
      p.title.toLowerCase().includes(search.toLowerCase())
    ), [plugins, search]);

  return (
    <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ p: '10px 14px', borderBottom: '1px solid #1e3a5f' }}>
        <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Plugin Filter</Typography>
        <TextField size="small" placeholder="Search plugins…" value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 14, color: '#4a5a70' }} /></InputAdornment> }}
          sx={{ width: '100%', '& .MuiInputBase-root': { backgroundColor: '#0d1825', color: '#c0cfe0', fontSize: 12 }, '& fieldset': { borderColor: '#1e3a5f' } }} />
      </Box>

      <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
        <Box onClick={() => onSelect(null)} sx={{
          px: 2, py: 1, cursor: 'pointer',
          backgroundColor: !selectedPlugin ? '#0d2137' : 'transparent',
          borderLeft: `3px solid ${!selectedPlugin ? '#00bcd4' : 'transparent'}`,
          '&:hover': { backgroundColor: '#0d1f33' },
        }}>
          <Typography sx={{ color: !selectedPlugin ? '#ffffff' : '#c0cfe0', fontSize: 12, fontWeight: !selectedPlugin ? 700 : 400 }}>All Plugins</Typography>
        </Box>

        {filtered.map(p => {
          const active = selectedPlugin === p.plugin_id;
          return (
            <Box key={p.plugin_id} onClick={() => onSelect(active ? null : p.plugin_id)} sx={{
              px: 2, py: '8px', cursor: 'pointer',
              backgroundColor: active ? '#0d2137' : 'transparent',
              borderLeft: `3px solid ${active ? '#00bcd4' : 'transparent'}`,
              '&:hover': { backgroundColor: '#0d1f33' },
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 11 }}>{p.plugin_id}</Typography>
                <Typography sx={{ color: '#4a5a70', fontSize: 11 }}>{p.count}</Typography>
              </Box>
              <Typography sx={{ color: active ? '#ffffff' : '#c0cfe0', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</Typography>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

// ── Stat Box ───────────────────────────────────────────────────────────────────

function StatBox({ label, value, color, active, onClick }) {
  return (
    <Paper onClick={onClick} sx={{
      flex: 1, backgroundColor: active ? `${color}11` : '#111827',
      border: `1px solid ${active ? color : '#1e3a5f'}`,
      borderRadius: 2, padding: '16px 20px', textAlign: 'center',
      cursor: onClick ? 'pointer' : 'default', transition: 'all 0.15s',
      '&:hover': onClick ? { border: `1px solid ${color}`, backgroundColor: `${color}11` } : {},
    }}>
      <Typography sx={{ color: color || '#ffffff', fontSize: 36, fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 0.5 }}>{label}</Typography>
    </Paper>
  );
}

// ── Filter Chip ────────────────────────────────────────────────────────────────

function FilterChip({ label, active, color, onClick }) {
  return (
    <Chip label={label} size="small" onClick={onClick} sx={{
      backgroundColor: active ? (color ? `${color}33` : '#0d2137') : '#0a1525',
      color:           active ? (color || '#00bcd4') : '#7a8fa6',
      border:          `1px solid ${active ? (color || '#00bcd4') : '#1e3a5f'}`,
      fontWeight:      active ? 700 : 400,
      cursor:          'pointer', fontSize: 12,
    }} />
  );
}

// ── Col Filter ─────────────────────────────────────────────────────────────────

function ColFilter({ label, options, value, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const isFiltered = value.length > 0;
  const toggle = (val) => onChange(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Typography sx={{ color: isFiltered ? '#00bcd4' : '#7a8fa6', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}{isFiltered ? ` (${value.length})` : ''}
      </Typography>
      {options.length > 0 && (
        <>
          <Box onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: isFiltered ? '#00bcd4' : '#1e3a5f', '&:hover': { color: '#00bcd4' } }}>
            <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
          </Box>
          <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
            PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f', minWidth: 160 } }}>
            {isFiltered && (
              <MenuItem onClick={() => { onChange([]); setAnchor(null); }}
                sx={{ fontSize: 11, color: '#f44336', borderBottom: '1px solid #1e3a5f', '&:hover': { backgroundColor: '#0d2137' } }}>
                Clear filter
              </MenuItem>
            )}
            {options.map(opt => {
              const selected = value.includes(opt);
              return (
                <MenuItem key={opt} onClick={() => toggle(opt)}
                  sx={{ fontSize: 12, color: selected ? '#00bcd4' : '#c0cfe0', backgroundColor: selected ? '#0d2137' : 'transparent', '&:hover': { backgroundColor: '#0d2137' }, display: 'flex', justifyContent: 'space-between' }}>
                  {opt}
                  {selected && <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#00bcd4' }} />}
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
}

// ── Vuln Row ───────────────────────────────────────────────────────────────────

function VulnRow({ v, onPatch }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Paper sx={{
      backgroundColor: '#111827', border: '1px solid #1e3a5f',
      borderLeft: `3px solid ${SEV_COLOR[v.severity] || '#607d8b'}`,
      borderRadius: 1, '&:hover': { backgroundColor: '#0d1f33' },
    }}>
      <Box onClick={() => setExpanded(e => !e)} sx={{
        display: 'grid', gridTemplateColumns: '130px 1fr 110px 80px 80px 32px',
        alignItems: 'center', padding: '10px 14px', gap: 1, cursor: 'pointer',
      }}>
        <Box>
          <Typography sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 11 }}>{v.plugin_id}</Typography>
          {v.cve_id && <Typography sx={{ color: '#4a5a70', fontFamily: 'monospace', fontSize: 10 }}>{v.cve_id}</Typography>}
        </Box>
        <Box>
          <Typography sx={{ color: '#c0cfe0', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.title}</Typography>
          {v.asset_hostname && (
            <Typography sx={{ color: '#4a5a70', fontSize: 10, fontFamily: 'monospace' }}>{v.asset_hostname}</Typography>
          )}
        </Box>
        <Chip label={SEV_LABEL[v.severity] || v.severity} size="small" sx={{
          backgroundColor: `${SEV_COLOR[v.severity] || '#607d8b'}22`,
          color: SEV_COLOR[v.severity] || '#607d8b',
          fontWeight: 700, fontSize: 11, height: 20, width: 'fit-content',
        }} />
        <Typography sx={{ color: v.cvss_score >= 9 ? '#f44336' : v.cvss_score >= 7 ? '#ff9800' : '#c0cfe0', fontWeight: 700, fontSize: 13 }}>
          {v.cvss_score ? v.cvss_score.toFixed(1) : '—'}
        </Typography>
        <Chip label={v.patched ? 'Patched' : 'Open'} size="small"
          onClick={(e) => { e.stopPropagation(); if (!v.patched) onPatch(v.id); }}
          sx={{ backgroundColor: v.patched ? '#0a2a1a' : '#2a0a0a', color: v.patched ? '#4caf50' : '#f44336', fontWeight: 700, fontSize: 10, height: 18, cursor: v.patched ? 'default' : 'pointer' }} />
        <Box sx={{ color: '#4a5a70', display: 'flex', alignItems: 'center' }}>
          {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Divider sx={{ borderColor: '#1e3a5f' }} />
        <Box sx={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 1.5, backgroundColor: '#0a0e1a' }}>
          {v.description && (
            <Box>
              <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Description</Typography>
              <Typography sx={{ color: '#c0cfe0', fontSize: 12, whiteSpace: 'pre-wrap' }}>{v.description}</Typography>
            </Box>
          )}
          {v.solution && (
            <Box>
              <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Solution</Typography>
              <Typography sx={{ color: '#4caf50', fontSize: 12, whiteSpace: 'pre-wrap' }}>{v.solution}</Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {v.first_seen && <Box><Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>First Seen</Typography><Typography sx={{ color: '#c0cfe0', fontSize: 11 }}>{new Date(v.first_seen).toLocaleDateString()}</Typography></Box>}
            {v.last_seen  && <Box><Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Last Seen</Typography><Typography sx={{ color: '#c0cfe0', fontSize: 11 }}>{new Date(v.last_seen).toLocaleDateString()}</Typography></Box>}
            {v.patch_available && <Box><Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Patch</Typography><Typography sx={{ color: '#4caf50', fontSize: 11 }}>Available</Typography></Box>}
            <Box><Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Source</Typography><Typography sx={{ color: '#c0cfe0', fontSize: 11, fontFamily: 'monospace' }}>{v.source || 'tenable'}</Typography></Box>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}

// ── Import Dialog ──────────────────────────────────────────────────────────────

function ImportDialog({ open, onClose, onImported }) {
  const [files, setFiles]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus]   = useState('');
  const fileRef               = useRef();

  useEffect(() => {
    if (open) {
      setStatus('');
      listSampleFiles().then(r => setFiles(r.files || [])).catch(() => setFiles([]));
    }
  }, [open]);

  const handleImport = async (filename) => {
    setLoading(true); setStatus('');
    try {
      const r = await importScanFile(filename);
      setStatus(`✓ ${r.scan_type} scan imported — ${r.hosts_in_file} hosts, ${r.vulns_created ?? r.assets_created ?? r.checks_created ?? 0} records created`);
      onImported();
    } catch (e) { setStatus('Import failed: ' + e.message); }
    finally { setLoading(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true); setStatus('');
    try {
      const r = await uploadScanFile(file);
      setStatus(`✓ ${r.scan_type} scan uploaded — ${r.hosts_in_file} hosts, ${r.vulns_created ?? r.assets_created ?? r.checks_created ?? 0} records created`);
      onImported();
    } catch (e) { setStatus('Upload failed: ' + e.message); }
    finally { setLoading(false); e.target.value = ''; }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f' } }}>
      <DialogTitle sx={{ color: '#ffffff', borderBottom: '1px solid #1e3a5f', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Import Nessus Scan
        <IconButton onClick={onClose} sx={{ color: '#7a8fa6' }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Upload File</Typography>
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={loading}
            sx={{ borderColor: '#00bcd4', color: '#00bcd4', fontSize: 12, '&:hover': { backgroundColor: '#00bcd411' } }}>
            Choose .nessus file
            <input ref={fileRef} type="file" accept=".nessus" hidden onChange={handleUpload} />
          </Button>
        </Box>
        <Divider sx={{ borderColor: '#1e3a5f', my: 2 }} />
        <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>Sample Files (Server)</Typography>
        {files.length === 0
          ? <Typography sx={{ color: '#4a5a70', fontSize: 12 }}>No sample files found on server.</Typography>
          : <List dense disablePadding>
              {files.map(f => (
                <ListItem key={f} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton disabled={loading} onClick={() => handleImport(f)}
                    sx={{ backgroundColor: '#0d1825', border: '1px solid #1e3a5f', borderRadius: 1, '&:hover': { backgroundColor: '#0d2137', borderColor: '#00bcd4' } }}>
                    <ListItemText primary={f} primaryTypographyProps={{ sx: { color: '#c0cfe0', fontSize: 13, fontFamily: 'monospace' } }}
                      secondary={f.includes('discovery') ? 'Host discovery' : f.includes('vuln') ? 'Vulnerability scan' : f.includes('config') ? 'Configuration scan' : ''}
                      secondaryTypographyProps={{ sx: { color: '#4a5a70', fontSize: 11 } }} />
                    {loading ? <CircularProgress size={14} sx={{ color: '#00bcd4' }} /> : null}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>}
        {status && (
          <Typography sx={{ color: status.startsWith('✓') ? '#4caf50' : '#f44336', fontSize: 12, mt: 2 }}>{status}</Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function Vulnerabilities({ onNavigate }) {
  const [tenableStatus,     setTenableStatus]     = useState(null);
  const [vulns,             setVulns]             = useState([]);
  const [summary,           setSummary]           = useState(null);
  const [credentialedData,  setCredentialedData]  = useState(null);
  const [plugins,           setPlugins]           = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [syncing,           setSyncing]           = useState(false);
  const [syncMsg,           setSyncMsg]           = useState('');
  const [importOpen,        setImportOpen]        = useState(false);
  const [filterSev,         setFilterSev]         = useState('all');
  const [filterPatched,     setFilterPatched]     = useState('open');
  const [filterSevCol,      setFilterSevCol]      = useState([]);
  const [selectedPlugin,    setSelectedPlugin]    = useState(null);
  const [filterCred,        setFilterCred]        = useState('all');
  const [viewMode,          setViewMode]          = useState('findings');

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([
      getTenableStatus(),
      getVulnSummary(),
      getVulns({ limit: 500 }),
      getCredentialedScan().catch(() => null),
      getPluginList().catch(() => []),
    ]).then(([status, sum, vulnData, credData, pluginData]) => {
      setTenableStatus(status);
      setSummary(sum);
      setVulns(vulnData.vulnerabilities || []);
      setCredentialedData(credData);
      setPlugins(pluginData || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSync = async () => {
    setSyncing(true); setSyncMsg('');
    try {
      const [ar, vr] = await Promise.all([syncAssets(), syncVulns()]);
      setSyncMsg(`Synced: ${ar.created + ar.updated} assets, ${vr.created + vr.updated} vulns`);
      loadData();
    } catch (e) { setSyncMsg('Sync failed: ' + e.message); }
    finally { setSyncing(false); }
  };

  const handlePatch = async (vulnId) => {
    await markVulnPatched(vulnId);
    setVulns(prev => prev.map(v => v.id === vulnId ? { ...v, patched: true } : v));
  };

  const credGoodIds = useMemo(() => new Set(
    (credentialedData?.hosts || []).filter(h => h.status === 'good' || h.status === 'remediate').map(h => h.asset_id)
  ), [credentialedData]);

  const credBadIds = useMemo(() => new Set(
    (credentialedData?.hosts || []).filter(h => h.status === 'bad').map(h => h.asset_id)
  ), [credentialedData]);

  const filtered = useMemo(() => vulns.filter(v => {
    if (filterSev !== 'all' && v.severity !== filterSev) return false;
    if (filterPatched === 'open' && v.patched) return false;
    if (filterPatched === 'patched' && !v.patched) return false;
    if (filterSevCol.length > 0 && !filterSevCol.includes(v.severity)) return false;
    if (selectedPlugin && v.plugin_id !== selectedPlugin) return false;
    if (filterCred === 'credentialed' && !credGoodIds.has(v.asset_id)) return false;
    if (filterCred === 'uncredentialed' && !credBadIds.has(v.asset_id)) return false;
    return true;
  }), [vulns, filterSev, filterPatched, filterSevCol, selectedPlugin, filterCred, credGoodIds, credBadIds]);

  const configured = tenableStatus?.configured;
  const lastSync   = tenableStatus?.last_sync ? new Date(tenableStatus.last_sync).toLocaleString() : null;

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: 3 }}>

      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: '8px 14px', backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2 }}>
        <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Tenable</Typography>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: configured ? '#4caf50' : '#4a5a70' }} />
        <Typography sx={{ color: '#ffffff', fontSize: 12, fontWeight: 600 }}>
          {configured ? (tenableStatus?.mode === 'sc' ? 'Security Center' : 'Tenable.io') : 'Not Configured'}
        </Typography>
        {lastSync && <Typography sx={{ color: '#7a8fa6', fontSize: 11 }}>· Last sync: {lastSync}</Typography>}
        {syncMsg && <Typography sx={{ color: syncMsg.startsWith('Sync failed') ? '#f44336' : '#4caf50', fontSize: 11 }}>{syncMsg}</Typography>}
        <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 1 }}>
          <Button onClick={() => setImportOpen(true)} variant="outlined" size="small" startIcon={<UploadFileIcon sx={{ fontSize: 14 }} />}
            sx={{ borderColor: '#1e3a5f', color: '#7a8fa6', fontSize: 11, '&:hover': { borderColor: '#00bcd4', color: '#00bcd4' } }}>
            Import Scan
          </Button>
          {configured && (
            <Button onClick={handleSync} disabled={syncing} variant="outlined" size="small" startIcon={syncing ? <CircularProgress size={12} /> : <SyncIcon sx={{ fontSize: 14 }} />}
              sx={{ borderColor: '#00bcd4', color: '#00bcd4', fontSize: 11, '&:hover': { backgroundColor: '#00bcd411' } }}>
              {syncing ? 'Syncing…' : 'Sync Now'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Plugin 19506 Banner */}
      {credentialedData && credentialedData.total > 0 && (
        <CredentialedBanner data={credentialedData} onViewDetails={() => onNavigate?.('cred_scan')} />
      )}

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {['critical', 'high', 'medium', 'low'].map(s => (
          <StatBox key={s} label={SEV_LABEL[s]} value={summary?.[s] ?? 0} color={SEV_COLOR[s]}
            active={filterSev === s} onClick={() => setFilterSev(prev => prev === s ? 'all' : s)} />
        ))}
      </Box>

      {/* View toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, overflow: 'hidden' }}>
          {[
            { key: 'findings', label: 'Findings View' },
            { key: 'devices',  label: 'Device View'   },
          ].map((opt, i) => {
            const active = viewMode === opt.key;
            return (
              <Box key={opt.key} onClick={() => setViewMode(opt.key)} sx={{
                px: 2.5, py: 1, cursor: 'pointer',
                backgroundColor: active ? '#00bcd422' : 'transparent',
                borderRight: i === 0 ? '1px solid #1e3a5f' : 'none',
                '&:hover': { backgroundColor: active ? '#00bcd422' : '#0d1825' },
                transition: 'background 0.15s',
              }}>
                <Typography sx={{ color: active ? '#00bcd4' : '#7a8fa6', fontSize: 13, fontWeight: active ? 700 : 400 }}>
                  {opt.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {viewMode === 'devices' ? (
        <VulnDeviceView />
      ) : (
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>

        {/* Left sidebar: severity/status filters + plugin filter */}
        <Box sx={{ width: 220, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, p: 2 }}>
            <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>Severity</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5 }}>
              {['all', 'critical', 'high', 'medium', 'low'].map(s => (
                <FilterChip key={s} label={s === 'all' ? 'All' : SEV_LABEL[s]} active={filterSev === s} color={SEV_COLOR[s]} onClick={() => setFilterSev(s)} />
              ))}
            </Box>
            <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>Status</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5 }}>
              {[['all', 'All', undefined], ['open', 'Open', '#f44336'], ['patched', 'Patched', '#4caf50']].map(([val, lbl, col]) => (
                <FilterChip key={val} label={lbl} active={filterPatched === val} color={col} onClick={() => setFilterPatched(val)} />
              ))}
            </Box>

            <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>19506 Credentialed</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              <FilterChip label="All"            active={filterCred === 'all'}            color={undefined}  onClick={() => setFilterCred('all')} />
              <FilterChip label="Credentialed"   active={filterCred === 'credentialed'}   color="#4caf50"    onClick={() => setFilterCred('credentialed')} />
              <FilterChip label="Uncredentialed" active={filterCred === 'uncredentialed'} color="#f44336"    onClick={() => setFilterCred('uncredentialed')} />
            </Box>
          </Paper>

          {plugins.length > 0 && (
            <PluginFilter plugins={plugins} selectedPlugin={selectedPlugin} onSelect={setSelectedPlugin} />
          )}
        </Box>

        {/* Vuln Table */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '130px 1fr 110px 80px 80px 32px', padding: '8px 14px', mb: 0.5 }}>
            <ColFilter label="Plugin / CVE" options={[]} value={[]} onChange={() => {}} />
            <ColFilter label="Title / Host"  options={[]} value={[]} onChange={() => {}} />
            <ColFilter label="Severity" options={['critical', 'high', 'medium', 'low']} value={filterSevCol} onChange={setFilterSevCol} />
            <ColFilter label="CVSS"     options={[]} value={[]} onChange={() => {}} />
            <ColFilter label="Status"   options={[]} value={[]} onChange={() => {}} />
            <Box />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <CircularProgress size={32} sx={{ color: '#00bcd4' }} />
            </Box>
          ) : filtered.length === 0 ? (
            <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, p: 4, textAlign: 'center' }}>
              <Typography sx={{ color: '#4a5a70', fontSize: 14 }}>
                {vulns.length === 0
                  ? 'No vulnerabilities found. Import a scan file or connect Tenable to pull data.'
                  : 'No vulnerabilities match the current filters.'}
              </Typography>
              {vulns.length === 0 && (
                <Button onClick={() => setImportOpen(true)} variant="outlined" size="small" startIcon={<UploadFileIcon />}
                  sx={{ mt: 2, borderColor: '#00bcd4', color: '#00bcd4', fontSize: 12 }}>
                  Import Scan
                </Button>
              )}
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {filtered.map(v => <VulnRow key={v.id} v={v} onPatch={handlePatch} />)}
            </Box>
          )}

          <Typography sx={{ color: '#4a5a70', fontSize: 12, mt: 1.5 }}>
            Showing {filtered.length} of {vulns.length} findings
          </Typography>
        </Box>
      </Box>
      )}

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={loadData} />
    </Box>
  );
}
